import { Form, Input, message, Space } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../../../hooks/useAuth';
import { useAppDispatch } from '../../../../store';
import {
  createPlanogram,
  updatePlanogram,
} from '../../../../store/features/planogram/planogramSlice';
import { UserPermissions } from '../../../../util';
import Button from '../../../elements/Button';
import FormWrapper from '../../../elements/FormWrapper';
import NoPermission from '../../../elements/NoPermission';
import { RoutePath } from '../../../views/AppRoot/types';

interface LocationState {
  data?: {
    uuid: string;
    name: string;
    description: string;
    [key: string]: any;
  };
}

const PlanogramForm = () => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const projectData = location.state?.data;
  const isEditMode = !!projectData;

  useEffect(() => {
    if (isEditMode && projectData) {
      form.setFieldsValue({
        name: projectData.name,
        description: projectData.description,
      });
    }

    return () => {
      form.resetFields();
    };
  }, [form, isEditMode, projectData]);

  const handleSubmit = useCallback(
    async (values: { name: string; description: string }) => {
      try {
        setSubmitting(true);

        if (isEditMode && projectData) {
          await dispatch(
            updatePlanogram({
              planogramId: projectData.uuid,
              planogram: values,
            })
          ).unwrap();

          message.success('Project updated successfully');
        } else {
          await dispatch(createPlanogram(values)).unwrap();
          message.success('Project created successfully');
        }

        history.push(RoutePath.Planogram);
      } catch (error) {
        message.error(`Failed to ${isEditMode ? 'update' : 'create'} project`);
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, history, isEditMode, projectData]
  );

  if (
    !hasPermission(
      isEditMode
        ? UserPermissions.PlanogramProjectsEdit
        : UserPermissions.PlanogramProjectsCreate
    )
  ) {
    return <NoPermission />;
  }

  return (
    <FormWrapper
      title={isEditMode ? `Edit Project` : 'Create New Planogram Project'}
      onClose={() => history.goBack()}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label="Project Name"
          rules={[{ required: true, message: 'Project name is required' }]}
        >
          <Input placeholder="Enter project name" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea
            placeholder="Enter project description (optional)"
            rows={6}
          />
        </Form.Item>
        <Form.Item>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Button
              loading={submitting}
              htmlType="submit"
              type="primary"
              style={{ width: '100%' }}
            >
              {isEditMode ? 'Update Project' : 'Create Project'}
            </Button>
            <Button onClick={() => history.goBack()} style={{ width: '100%' }}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </FormWrapper>
  );
};

export default PlanogramForm;
